import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { TextField } from "@mui/material";

import Button from "components/Button";
import documentFootersService, {
  IDocumentFooterGroup,
  TDocumentFooterSlot,
  documentFootersQueryKeys,
} from "api/documentFootersService";
import { IError } from "dto/IError";
import { ENotificationType, notification } from "utils/notification";

import styles from "./styles.module.scss";

// Przybornik nagłówków i stopek graficznych (port zakładki „Przybornik stopek" z ustawień
// dokumentów Magistratu — DefaultDocumentSettingsControl). Po lewej zestawy (nazwa,
// dodaj/zmień nazwę/usuń), po prawej cztery sloty grafik wybranego zestawu z podglądem
// i Dodaj/Usuń. Pracownik wstawia zestaw w Wordzie: Nota → Elementy dokumentu →
// Nagłówek i stopka; dodatek czyta te same endpointy.

const TITLE = "Nagłówki i stopki";
const MAX_IMAGE_SIZE = 2 * 1024 * 1024;

interface ISlotDef {
  slot: TDocumentFooterSlot;
  label: string;
  /** Tekst w pustym slocie — jak podpisy w Magistracie. */
  empty: string;
  has: (g: IDocumentFooterGroup) => boolean;
}

const SLOTS: ISlotDef[] = [
  {
    slot: "PageHeader",
    label: "Nagłówek strony",
    empty: "Brak nagłówka",
    has: (g) => g.HasPageHeader,
  },
  {
    slot: "FirstPageHeader",
    label: "Nagłówek pierwszej strony",
    empty: "Taki sam jak na pozostałych stronach",
    has: (g) => g.HasFirstPageHeader,
  },
  {
    slot: "PageFooter",
    label: "Stopka strony",
    empty: "Brak stopki",
    has: (g) => g.HasPageFooter,
  },
  {
    slot: "FirstPageFooter",
    label: "Stopka pierwszej strony",
    empty: "Taka sama jak na pozostałych stronach",
    has: (g) => g.HasFirstPageFooter,
  },
];

const errorText = (error: IError | null | undefined, fallback: string) =>
  error?.response?.data?.errorCodes?.[0]?.message ?? fallback;

/** Podgląd grafiki slotu: blob z API → object URL (zwalniany przy zmianie/odmontowaniu). */
const SlotPreview = ({
  group,
  def,
}: {
  group: IDocumentFooterGroup;
  def: ISlotDef;
}) => {
  const has = def.has(group);
  const { data, isLoading } = documentFootersService.useGetImage(
    group.Id,
    def.slot,
    has,
    group.LastModified,
  );
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!data?.data) {
      setUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(data.data);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [data]);

  if (!has) {
    return <div className={styles.slotEmpty}>{def.empty}</div>;
  }

  if (isLoading || !url) {
    return <div className={styles.slotEmpty}>Ładowanie…</div>;
  }

  return <img className={styles.slotImage} src={url} alt={def.label} />;
};

const DocumentFootersTab = () => {
  const queryClient = useQueryClient();
  const { data: listData, isLoading } = documentFootersService.useGetList();
  const { mutate: save, isLoading: isSaving } = documentFootersService.useSave();
  const { mutate: remove } = documentFootersService.useRemove();
  const { mutate: uploadImage, isLoading: isUploading } =
    documentFootersService.useUploadImage();
  const { mutate: removeImage } = documentFootersService.useRemoveImage();

  const items = useMemo(() => listData?.data?.Items ?? [], [listData]);
  const canEdit = listData?.data?.CanEdit ?? false;

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [uploadingSlot, setUploadingSlot] = useState<TDocumentFooterSlot | null>(
    null,
  );

  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingSlotRef = useRef<TDocumentFooterSlot | null>(null);

  // Zaznaczenie podąża za listą: pierwszy zestaw domyślnie, usunięty znika z wyboru.
  useEffect(() => {
    if (items.length === 0) {
      setSelectedId(null);
    } else if (!selectedId || !items.some((g) => g.Id === selectedId)) {
      setSelectedId(items[0].Id);
    }
  }, [items, selectedId]);

  const selected = items.find((g) => g.Id === selectedId) ?? null;

  const refresh = () =>
    queryClient.invalidateQueries(documentFootersQueryKeys.list);

  const fail = (text: string) =>
    notification({ type: ENotificationType.ERROR, title: TITLE, text });

  const handleAdd = () => {
    const name = newName.trim();
    if (!name) return;
    save(
      { Name: name },
      {
        onSuccess: (res) => {
          setNewName("");
          refresh();
          setSelectedId(res.data.Id);
        },
        onError: (error) =>
          fail(errorText(error, "Nie udało się dodać zestawu")),
      },
    );
  };

  const startRename = (g: IDocumentFooterGroup) => {
    setRenamingId(g.Id);
    setRenameValue(g.Name);
  };

  const commitRename = () => {
    if (!renamingId) return;
    const name = renameValue.trim();
    const current = items.find((g) => g.Id === renamingId);
    if (!name || !current || name === current.Name) {
      setRenamingId(null);
      return;
    }
    save(
      { Id: renamingId, Name: name },
      {
        onSuccess: () => {
          setRenamingId(null);
          refresh();
        },
        onError: (error) =>
          fail(errorText(error, "Nie udało się zmienić nazwy zestawu")),
      },
    );
  };

  const handleRemove = (g: IDocumentFooterGroup) => {
    if (
      !window.confirm(
        `Czy na pewno chcesz usunąć zestaw „${g.Name}"? Dokumenty, do których został już wstawiony, nie zmienią się.`,
      )
    )
      return;
    remove(g.Id, {
      onSuccess: () => refresh(),
      onError: (error) =>
        fail(errorText(error, "Nie udało się usunąć zestawu")),
    });
  };

  const pickFile = (slot: TDocumentFooterSlot) => {
    pendingSlotRef.current = slot;
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleFileChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const slot = pendingSlotRef.current;
    pendingSlotRef.current = null;
    if (!file || !slot || !selected) return;

    if (!["image/png", "image/jpeg"].includes(file.type)) {
      fail("Dozwolone są pliki graficzne PNG i JPEG");
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      fail("Maksymalny rozmiar grafiki to 2 MB");
      return;
    }

    setUploadingSlot(slot);
    uploadImage(
      { id: selected.Id, slot, file },
      {
        onSuccess: () => {
          refresh();
          // Podgląd slotu ma własne zapytanie (staleTime: Infinity) — po wgraniu
          // trzeba je unieważnić, bo LastModified w kluczu ma rozdzielczość sekundy.
          queryClient.invalidateQueries([
            documentFootersQueryKeys.image,
            selected.Id,
            slot,
          ]);
        },
        onError: (error) =>
          fail(errorText(error, "Nie udało się wgrać grafiki")),
        onSettled: () => setUploadingSlot(null),
      },
    );
  };

  const handleRemoveImage = (slot: TDocumentFooterSlot) => {
    if (!selected) return;
    removeImage(
      { id: selected.Id, slot },
      {
        onSuccess: () => refresh(),
        onError: (error) =>
          fail(errorText(error, "Nie udało się usunąć grafiki")),
      },
    );
  };

  const renderRow = (g: IDocumentFooterGroup) => {
    const isSelected = g.Id === selectedId;
    const isRenaming = g.Id === renamingId;
    const filled = SLOTS.filter((s) => s.has(g)).length;

    return (
      <div
        key={g.Id}
        className={`${styles.row} ${isSelected ? styles.rowSelected : ""}`}
        onClick={() => setSelectedId(g.Id)}
      >
        <div className={styles.rowMain}>
          {isRenaming ? (
            <TextField
              size="small"
              fullWidth
              autoFocus
              value={renameValue}
              inputProps={{ maxLength: 200 }}
              onChange={(e) => setRenameValue(e.target.value)}
              onBlur={commitRename}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitRename();
                if (e.key === "Escape") setRenamingId(null);
              }}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <>
              <div className={styles.rowTitle}>{g.Name}</div>
              <div className={styles.rowMeta}>
                {filled === 0 ? "bez grafik" : `grafiki: ${filled} z 4`}
              </div>
            </>
          )}
        </div>
        {canEdit && !isRenaming && (
          <div className={styles.rowActions}>
            <Button text="Zmień nazwę" onClick={() => startRename(g)} />
            <Button text="Usuń" onClick={() => handleRemove(g)} />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.section}>
        <div className={styles.sectionTitle}>{TITLE}</div>
        <div className={styles.hint}>
          Zestaw to cztery grafiki: nagłówek i stopka strony oraz — jeśli mają
          być inne — nagłówek i stopka pierwszej strony. Pracownik wstawia
          zestaw do dokumentu w Wordzie: zakładka Nota → Elementy dokumentu →
          Nagłówek i stopka. Grafika wstawiana jest na całą szerokość między
          marginesami, więc najlepiej przygotować ją w proporcjach docelowego
          nagłówka (PNG albo JPEG, do 2 MB).
        </div>
      </div>

      <div className={styles.columns}>
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Zestawy</div>
          {isLoading && <div className={styles.hint}>Ładowanie…</div>}
          {!isLoading && items.length === 0 && (
            <div className={styles.hint}>
              Brak zestawów.
              {canEdit
                ? " Dodaj pierwszy poniżej."
                : " Zestawy definiuje właściciel firmy albo osoba z uprawnieniem do edycji ustawień firmy."}
            </div>
          )}
          <div className={styles.list}>{items.map(renderRow)}</div>

          {canEdit && (
            <div className={styles.addRow}>
              <TextField
                size="small"
                fullWidth
                label="Nazwa nowego zestawu"
                value={newName}
                inputProps={{ maxLength: 200 }}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAdd();
                }}
              />
              <Button
                text="Dodaj zestaw"
                disabled={!newName.trim() || isSaving}
                onClick={handleAdd}
              />
            </div>
          )}
        </div>

        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            {selected ? `Grafiki zestawu „${selected.Name}"` : "Grafiki zestawu"}
          </div>
          {!selected && (
            <div className={styles.hint}>
              Wybierz zestaw z listy, żeby zobaczyć jego grafiki.
            </div>
          )}
          {selected && (
            <div className={styles.slots}>
              {SLOTS.map((def) => {
                const has = def.has(selected);
                const busy = uploadingSlot === def.slot && isUploading;
                return (
                  <div key={def.slot} className={styles.slot}>
                    <div className={styles.slotLabel}>{def.label}</div>
                    <div className={styles.slotPreview}>
                      <SlotPreview group={selected} def={def} />
                    </div>
                    {canEdit && (
                      <div className={styles.slotActions}>
                        <Button
                          text={has ? "Zmień" : "Dodaj"}
                          loading={busy}
                          disabled={isUploading}
                          onClick={() => pickFile(def.slot)}
                        />
                        {has && (
                          <Button
                            text="Usuń"
                            disabled={isUploading}
                            onClick={() => handleRemoveImage(def.slot)}
                          />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg"
        className={styles.fileInput}
        onChange={handleFileChosen}
      />
    </div>
  );
};

export default DocumentFootersTab;
